import { z } from 'zod';
import type { GraphDocument } from '@/domain/funnel/graph-document';

export const EXPORT_FORMAT = 'dark-funnels' as const;
export const EXPORT_FORMAT_VERSION = 1 as const;
export const MAX_IMPORT_BYTES = 5 * 1024 * 1024;

export const ExportFormatSchema = z.enum(['png', 'pdf', 'json']);
export const ExportScopeSchema = z.enum(['full_graph', 'current_view', 'presentation']);
export const ImageScaleSchema = z.union([z.literal(1), z.literal(2), z.literal(3)]);
export const BackgroundModeSchema = z.enum(['solid', 'transparent']);

export type ExportFormat = z.infer<typeof ExportFormatSchema>;
export type ExportScope = z.infer<typeof ExportScopeSchema>;
export type ImageScale = z.infer<typeof ImageScaleSchema>;
export type BackgroundMode = z.infer<typeof BackgroundModeSchema>;

export interface DarkFunnelsExportPackage {
  format: typeof EXPORT_FORMAT;
  formatVersion: typeof EXPORT_FORMAT_VERSION;
  schemaVersion: number;
  exportedAt: string;
  metadata: { name: string; description?: string | null };
  graph: GraphDocument;
}

export const IMPORT_ERROR_CODES = [
  'IMPORT_INVALID_JSON',
  'IMPORT_FILE_TOO_LARGE',
  'IMPORT_UNSUPPORTED_FORMAT',
  'IMPORT_UNSUPPORTED_FORMAT_VERSION',
  'IMPORT_UNSUPPORTED_SCHEMA',
  'IMPORT_INVALID_GRAPH',
  'IMPORT_DANGLING_EDGE',
  'IMPORT_DUPLICATE_ID',
  'IMPORT_CREATE_FAILED',
] as const;
export type ImportErrorCode = (typeof IMPORT_ERROR_CODES)[number];

export type ImportWarning =
  | 'attachments_not_included'
  | 'logo_not_included'
  | 'unknown_node_type'
  | 'unknown_edge_type'
  | 'schema_migrated';

export interface ImportPreview {
  name: string;
  nodeCount: number;
  edgeCount: number;
  scenarioCount: number;
  formatVersion: number;
  schemaVersion: number;
  warnings: ImportWarning[];
}

export class ImportPackageError extends Error {
  constructor(
    readonly code: ImportErrorCode,
    message: string,
  ) {
    super(message);
    this.name = 'ImportPackageError';
  }
}
